import { Row, Col, message, Table, Pagination, Button, Popconfirm, Space, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { NavLink, useSearchParams } from "react-router-dom";
import { IPo } from "common/interface";
import { getAllPOs, markBillingCompleted, raiseBill, IBillingCompletedPayload } from "store/po/poSlice";
import { CompleteBillingModal } from "./components/CompleteBillingModal";
import { getDateAndTimeFromISODate, getDateFromISODate } from "common/utils";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilterButton, AppFilter, AppPoFilter } from "components/AppFilter";
import { getAllVendors } from "store/vendor/vendorSlice";

type PartialPOType = Partial<IPo> & { key: string };
const MESSAGE_KEY = "complete_billing_pos";

const headerKeys: { [key: string]: string } = {
  id: "PO ID",
  vendorId: "Vendor",
  orderDate: "Order Date",
  poCategory: "Category",
  receivedDate: "Received On",
  status: "Status",
  skuCount: "Item Count",
  companyTag: "Company Tag",
  totalAmount: "Total Amount",
  invoiceUrl: "Invoice",
  actions: "Actions",
};

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const CompletePOBillingPage = () => {
  useDocumentTitle("Complete PO Billing");
  const dispatch = useAppDispatch();
  const [columns, setColumns] = useState<ColumnsType<PartialPOType>>();
  const [rows, setRows] = useState<PartialPOType[]>();
  const { posList: pos, listStatus, listError } = useAppSelector((state) => state.po);
  const status = listStatus;
  const error = listError;
  const [filterState, setFilterState] = useState(false);
  const [searchParams] = useSearchParams();
  const [filterCount, setFilterCount] = useState(0);
  const [processingPoId, setProcessingPoId] = useState<string | null>(null);
  const [raisingBillPoId, setRaisingBillPoId] = useState<string | null>(null);
  const { vendors } = useAppSelector((state) => state.vendors);

  const getPOData = useCallback(
    (pageNum = 1, pageSize = 25, filter = "") => {
      const params = new URLSearchParams(filter);
      params.set("status", "BILLING_READY");
      dispatch(getAllPOs({ pageNo: pageNum, pageSize, filter: params.toString(), isGrnRequest: false }));
    },
    [dispatch]
  );

  const getVendorData = useCallback(
    (pageNum = 1, pageSize = 100, filter = "") => {
      if (vendors === undefined) {
        dispatch(getAllVendors({ pageNo: pageNum, pageSize, filter }));
      }
    },
    [dispatch]
  );

  const [billingModalPo, setBillingModalPo] = useState<PartialPOType | null>(null);

  const onRaiseBill = async (poId: string) => {
    const id = Number(poId);
    if (isNaN(id)) return;

    setRaisingBillPoId(poId);
    try {
      await dispatch(raiseBill({ poId: id })).unwrap();
      message.success({ content: "Bill raised successfully!", key: MESSAGE_KEY });
      getPOData(1, 25, searchParams.toString());
    } catch (err: any) {
      message.error({ content: err?.message || "Failed to raise bill", key: MESSAGE_KEY });
    } finally {
      setRaisingBillPoId(null);
    }
  };

  const onMarkBillingCompleted = async (poId: string, billingPayload: IBillingCompletedPayload) => {
    const id = Number(poId);
    if (isNaN(id)) return;

    setProcessingPoId(poId);
    try {
      await dispatch(markBillingCompleted({ poId: id, billingPayload })).unwrap();
      message.success({ content: "PO marked as billing completed!", key: MESSAGE_KEY });
      setBillingModalPo(null);
      getPOData(1, 25, searchParams.toString());
    } catch (err: any) {
      message.error({ content: err?.message || "Failed to mark billing completed", key: MESSAGE_KEY });
    } finally {
      setProcessingPoId(null);
    }
  };

  useEffect(() => {
    getVendorData();
  }, []);

  useEffect(() => {
    const filter = searchParams.toString();
    setFilterCount(
      Object.keys(Object.fromEntries(new URLSearchParams(filter))).length
    );
    getPOData(1, 25, filter);
  }, [dispatch, getPOData, searchParams]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Purchase Orders", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Purchase Orders loaded!", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  useEffect(() => {
    const poData = pos && pos.data ? pos.data : [];
    const data: PartialPOType[] = poData.map((po, i) => {
      const {
        id,
        poId,
        vendorId,
        vendorName,
        vendorCode,
        vendorDetails,
        poCategory,
        orderDate,
        expectedDeliveryDate,
        receivedDate,
        status,
        skuCount,
        totalAmount,
        companyTag,
        invoiceUrl,
      } = po;

      const newRow = {
        id,
        poId,
        vendorId,
        vendorName,
        vendorCode,
        vendorDetails,
        poCategory,
        orderDate,
        expectedDeliveryDate,
        receivedDate,
        status,
        skuCount,
        totalAmount,
        companyTag,
        invoiceUrl,
      };
      newRow.receivedDate = getDateAndTimeFromISODate(newRow.receivedDate);
      newRow.expectedDeliveryDate = getDateAndTimeFromISODate(newRow.expectedDeliveryDate);
      return { ...newRow, key: `po-billing-${i}`, ellipses: true };
    });

    const tableHeaders: ColumnsType<PartialPOType> = [
      ...Object.keys(headerKeys).map((key) => ({
        key,
        title: headerKeys[key],
        dataIndex: key,
        render: (text: string, record: PartialPOType) => {
          if (key === "actions") {
            const isBusy = processingPoId !== null || raisingBillPoId !== null;
            return (
              <Space direction="vertical" size={4}>
                {record.status === "BILLING_READY" && (
                  <Popconfirm
                    title="Are you sure you want to raise the bill for this PO?"
                    onConfirm={() => onRaiseBill(record.id!)}
                    disabled={isBusy}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="default"
                      size="small"
                      loading={raisingBillPoId === record.id}
                      disabled={isBusy && raisingBillPoId !== record.id}
                    >
                      Raise Bill
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  type="primary"
                  size="small"
                  loading={processingPoId === record.id}
                  disabled={isBusy && processingPoId !== record.id}
                  onClick={() => setBillingModalPo(record)}
                >
                  Complete Billing
                </Button>
              </Space>
            );
          }

          if (key === "invoiceUrl") {
            if (!record.invoiceUrl) return <>-</>;
            return (
              <Typography.Link href={record.invoiceUrl} target="_blank" rel="noopener noreferrer">
                Download
              </Typography.Link>
            );
          }

          if (key === "companyTag") {
            return <>{record.companyTag || "-"}</>;
          }

          if (key === "totalAmount") {
            return <>{record.totalAmount !== undefined ? `₹${record.totalAmount}` : "-"}</>;
          }

          if (key === "orderDate" || key === "expectedDeliveryDate") {
            return <div>{getDateFromISODate(record[key] || "")}</div>;
          }

          if (key === "vendorId") {
            return (
              <NavLink to={`/vendors/view/${record.vendorId}`}>{record.vendorCode} || {record.vendorName}</NavLink>
            );
          }

          if (key === "id") {
            return (
              <NavLink to={`/po/requests/${record.id}`}>{record.poId}</NavLink>
            );
          }

          return <>{text}</>;
        },
      })),
    ];

    setColumns(tableHeaders);
    setRows(data);
  }, [pos, processingPoId, raisingBillPoId]);

  const Paginator = () => {
    if (pos) {
      const { pageNo, pageSize, total } = pos;
      return (
        <StyledPagination
          current={pageNo}
          pageSize={pageSize}
          total={total}
          onChange={(page, size) => {
            const filter = searchParams.toString();
            getPOData(page, size, filter);
          }}
        />
      );
    }

    return <></>;
  };

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <AppPageHeader title={"Complete PO Billing"} />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState(!filterState)}
              filterCount={filterCount}
            />
          </div>
        </AppActionStrip>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppPoFilter />
        </AppFilter>
      </Row>
      {rows && rows.length ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading"}
              bordered
            />
          </Col>
          <Col xs={24}>
            <Paginator />
          </Col>
        </Row>
      ) : (
        status !== "loading" && <>No Purchase Orders with BILLING_READY status.</>
      )}
      <CompleteBillingModal
        visible={billingModalPo !== null}
        poId={billingModalPo?.poId}
        loading={processingPoId !== null}
        onSubmit={(payload) => onMarkBillingCompleted(billingModalPo!.id!, payload)}
        onCancel={() => setBillingModalPo(null)}
      />
    </>
  );
};

export default CompletePOBillingPage;
